import { useRef } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useTranslation from '@/utils/hooks/translation/useTranslation';
dayjs.extend(utc);
dayjs.extend(timezone);

// Utils
import { REMOVE_TOAST } from '@/hooks/context/toast/actions';
// Toast
import { useToastsContext } from '@/hooks/context/toast/useToastsContext';
//hooks
import { useNewFunnelActivated } from '@/utils/scheduler/funnel/hooks/useNewFunnelActivated';

import { put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Endpoint
import { EDIT_SCHEDULER_MASTER_POST, SCHEDULER_POST } from '@/lib/routes/apiEndpoints';
// Lib
import { calendarViews } from '@/lib/scheduler/calendar/calendar';

const getNewDataPost = ({
    oldDataPost,
    newPostScheduledAt,
    drop_date_formatted,
    updateCrossPosts
}) => {
    let newDataPost = {
        ...oldDataPost,
        scheduled_at: newPostScheduledAt,
        start: drop_date_formatted,
        startRef: drop_date_formatted,
        startEditable: true
    };

    if (updateCrossPosts === false && oldDataPost.cross_post_id !== null) {
        newDataPost = {
            ...newDataPost,
            cross_post_id: null,
            cross_post_social_profiles: []
        };
    }

    return newDataPost;
};

const useEventDrop = () => {
    const { t: tErrors } = useTranslation('errors');
    const { t: tScheduler } = useTranslation('scheduler');
    const toastId = useRef(null);
    const { toast, toastError, dispatchToasts } = useToastsContext();
    const { isNewFunnel } = useNewFunnelActivated();

    async function eventsHandling({
        event,
        schedulerPosts,
        mutateSchedulerPosts,
        timezone,
        updateCrossPosts = undefined
    }) {
        if (toastId.current) {
            dispatchToasts({
                type: REMOVE_TOAST,
                payload: toastId.current
            });
        }

        const postId = event?._def?.publicId;
        const masterId = event?._def?.extendedProps?.master_id;

        if (!postId) {
            return;
        }

        const view = event._context.viewApi.type ?? calendarViews.week;

        async function editPost(post, oldPost, updateCrossPosts, callbackSuccess) {
            const CancelToken = new AbortController();

            try {
                if (updateCrossPosts) {
                    await put(
                        injectEndpointUrlParams(EDIT_SCHEDULER_MASTER_POST, {
                            MASTER_ID: post.master_id
                        }),
                        { scheduled_at: post.scheduled_at, scheduled_at_option: 'custom' },
                        {
                            signal: CancelToken.signal
                        }
                    );
                } else {
                    await put(
                        injectEndpointUrlParams(SCHEDULER_POST, {
                            SPID: post.social_profile_id,
                            postId: post.id
                        }),
                        { ...post, scheduled_at_option: 'custom' },
                        {
                            signal: CancelToken.signal
                        }
                    );
                }
                if (typeof callbackSuccess === 'function') {
                    callbackSuccess();
                }
            } catch (e) {
                dispatchToasts({
                    type: REMOVE_TOAST,
                    payload: toastId.current
                });
                if (e?.message?.error === 'migration-upgrade-area-error') {
                    toastError({
                        title: t(
                            'scheduler:scheduler_funnel.submit_errors.migration_upgrade_area_error_banner'
                        ),
                        icon: 'calendar'
                    });
                } else {
                    toastError({
                        title: tErrors('error_occured_submit_data'),
                        icon: 'calendar'
                    });
                }

                if (oldPost) {
                    mutateSchedulerPosts();
                } else {
                    mutateSchedulerPosts(schedulerPosts, true);
                }
            }
        }

        const oldDataPost = schedulerPosts.find((post) => parseInt(postId) === parseInt(post.id));
        // Get hour/min/sec of the event
        const hourEvent = oldDataPost.startRef.split(' ')[1];
        const datePost = dayjs(oldDataPost.scheduled_at).utc().tz(timezone);
        let scheduleAtDate = `${dayjs(event.startStr).format('YYYY-MM-DDTHH:')}${datePost.format(
            'mm:ss'
        )}`;
        let startDate = event.startStr;

        if (view === calendarViews.month && event.source === null) {
            scheduleAtDate = `${event.startStr}T${datePost.format('HH:mm:ss')}`;
            startDate = `${event.startStr}T${hourEvent}`;
        }

        const utc = dayjs(startDate).utc().tz(timezone).format('Z');
        const drop_date = startDate.split('T');
        const drop_date_formatted = drop_date[0] + ' ' + drop_date[1];

        const currentDate = dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss')).add(5, 'minute').utc();
        const isScheduledBeforeCurrentDate = dayjs(scheduleAtDate + utc)
            .utc()
            .isBefore(currentDate);

        let newPostScheduledAt = dayjs(scheduleAtDate + utc)
            .utc()
            .format();
        if (isScheduledBeforeCurrentDate) {
            newPostScheduledAt = currentDate.format();
        }

        const getUpdatedPost = (post, newPostScheduledAt, drop_date_formatted) => ({
            ...post,
            scheduled_at: newPostScheduledAt,
            start: drop_date_formatted,
            startRef: drop_date_formatted,
            startEditable: true
        });

        mutateSchedulerPosts(
            schedulerPosts.map((post) => {
                if (updateCrossPosts && parseInt(masterId) === parseInt(post?.master_id)) {
                    return getUpdatedPost(post, newPostScheduledAt, drop_date_formatted);
                }

                if (!updateCrossPosts && parseInt(postId) === parseInt(post?.id)) {
                    return getUpdatedPost(post, newPostScheduledAt, drop_date_formatted);
                }

                return post;
            }),
            false
        );

        await editPost(
            {
                ...oldDataPost,
                scheduled_at: newPostScheduledAt,
                start: drop_date_formatted,
                startRef: drop_date_formatted,
                startEditable: true
            },
            oldDataPost,
            updateCrossPosts
        );
    }

    const oldEventsHandling = ({
        event,
        schedulerPosts,
        mutateSchedulerPosts,
        timezone,
        updateCrossPosts = undefined
    }) => {
        if (toastId.current) {
            dispatchToasts({
                type: REMOVE_TOAST,
                payload: toastId.current
            });
        }

        const calendarView = event._context.viewApi.type ?? calendarViews.week;
        const idPost = event?._def?.publicId;

        const submitPost = (post, oldPost, updateCrossPosts, callbackSuccess) => {
            const CancelToken = new AbortController();

            put(
                injectEndpointUrlParams(SCHEDULER_POST, {
                    SPID: post.social_profile_id,
                    postId: post.id
                }),
                { ...post, update_cross_posts: updateCrossPosts },
                {
                    signal: CancelToken.signal
                }
            )
                .then(() => {
                    if (typeof callbackSuccess === 'function') {
                        callbackSuccess();
                    }
                })
                .catch((e) => {
                    dispatchToasts({
                        type: REMOVE_TOAST,
                        payload: toastId.current
                    });
                    if (e?.message?.error === 'migration-upgrade-area-error') {
                        toastError({
                            title: t(
                                'scheduler:scheduler_funnel.submit_errors.migration_upgrade_area_error_banner'
                            ),
                            icon: 'calendar'
                        });
                    } else {
                        toastError({
                            title: tErrors('error_occured_submit_data'),
                            icon: 'calendar'
                        });
                    }

                    if (oldPost) {
                        mutateSchedulerPosts();
                    } else {
                        mutateSchedulerPosts(schedulerPosts, true);
                    }
                });

            return CancelToken;
        };

        if (idPost) {
            const oldDataPost = schedulerPosts.filter(
                (post) => parseInt(idPost) === parseInt(post.id)
            )[0];

            // Get hour/min/sec of the event
            const hourEvent = oldDataPost.startRef.split(' ')[1];
            const datePost = dayjs(oldDataPost.scheduled_at).utc().tz(timezone);

            // Get hour/min/sec of the event

            // If calendar view is month view and if the event is a stacked post add post hour at the end of the date
            let startDate = event.startStr;
            let scheduleAtDate = `${dayjs(event.startStr).format(
                'YYYY-MM-DDTHH:'
            )}${datePost.format('mm:ss')}`;
            if (calendarView === calendarViews.month && event.source === null) {
                startDate = `${event.startStr}T${hourEvent}`;
                scheduleAtDate = `${event.startStr}T${datePost.format('HH:mm:ss')}`;
            }

            const drop_date = startDate.split('T');
            const drop_date_formatted = drop_date[0] + ' ' + drop_date[1];
            const utc = dayjs(startDate).utc().tz(timezone).format('Z');

            const currentDate = dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss')).add(5, 'minute').utc();
            const isScheduledAtBeforeCurrentDate = dayjs(scheduleAtDate + utc)
                .utc()
                .isBefore(currentDate);

            const newPostScheduledAt = isScheduledAtBeforeCurrentDate
                ? currentDate.format()
                : dayjs(scheduleAtDate + utc)
                      .utc()
                      .format();

            let newDataPost = getNewDataPost({
                oldDataPost: oldDataPost,
                newPostScheduledAt: newPostScheduledAt,
                drop_date_formatted: drop_date_formatted,
                updateCrossPosts: updateCrossPosts
            });

            let crossPostId = oldDataPost?.cross_post_id;
            mutateSchedulerPosts(
                schedulerPosts.map((post) => {
                    if (parseInt(idPost) === parseInt(post.id)) {
                        return newDataPost;
                    } else if (
                        updateCrossPosts &&
                        crossPostId &&
                        post?.cross_post_id === crossPostId
                    ) {
                        return getNewDataPost({
                            oldDataPost: post,
                            newPostScheduledAt: newPostScheduledAt,
                            drop_date_formatted: drop_date_formatted,
                            updateCrossPosts: updateCrossPosts
                        });
                    } else if (updateCrossPosts === false && post?.cross_post_id === crossPostId) {
                        const cross_post_social_profiles = post.cross_post_social_profiles.filter(
                            (crossPostSocialProfile) =>
                                crossPostSocialProfile.id !== oldDataPost.social_profile_id
                        );
                        return {
                            ...post,
                            cross_post_social_profiles: cross_post_social_profiles,
                            cross_post_id: cross_post_social_profiles.length
                                ? post.cross_post_id
                                : null
                        };
                    }
                    return post;
                }),
                false
            );

            const updateEvent = submitPost(newDataPost, oldDataPost, updateCrossPosts);

            /**
             * Add setTimeout cause there is a bug on the toast transition cause of calendar
             */
            setTimeout(() => {
                toastId.current = toast(
                    {
                        title: tScheduler('toast_undo_drag_and_drop'),
                        icon: 'calendar'
                    },
                    {
                        progressBar: {
                            show: true
                        },
                        cancelAction: () => {
                            // Cancel request
                            updateEvent.abort();

                            // Rollback to old slot
                            submitPost(oldDataPost, oldDataPost, updateCrossPosts, () =>
                                mutateSchedulerPosts()
                            );
                        }
                    }
                );
            }, 300);
        }
    };

    return { eventsHandling: isNewFunnel ? eventsHandling : oldEventsHandling };
};

export default useEventDrop;
